import React, { useContext, useMemo, useState } from 'react';
import { t } from '../../../../types/translation/Translator';
import { CompanyRoleAssignmentContext } from '../../../../context/CompanyRoleAssignmentContext';
import {
  CompanyInviteState,
  CompanyRole,
  CompanyRoleAssignment,
} from '../../../../types/company_role_assignment/companyRoleAssignment';
import InviteUserModal from './Modals/InviteUserModal';
import { CompanyContext } from '../../../../context/CompanyContext';
import { useNavigate } from 'react-router-dom';
import UserImportModal from './Modals/UserImportModal';
import { UserContext } from '../../../../context/UserContext';
import { StockLocationRoleAssignmentContext } from '../../../../context/StockLocationRoleAssignmentContext';
import { StockLocationRole } from '../../../../types/stock_location_role_assignment/stockLocationRoleAssignment';
import Table from '../../../components/Table';
import { CompanyRoleAssignmentFilter } from '../../../../VentoryUI/components/filters/CompanyRoleAssignmentFilter';
import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { SearchFilter } from '../../../components/Filter';
import Icon, { IconType } from '../../../icons/Icon';
import { Button, ImportIconButton } from '../../../components/Button';
import { Routing } from '../../../../util/routes.util';
import { CompanyRoleAssignmentUtil } from '../../../../types/company_role_assignment/companyRoleAssignment.util';
import { SettingsIconButton } from '../../../icons';

export default function UserSettingsScreen() {
  const navigate = useNavigate();

  const { companyRoles, hasCompanyRole, isAdmin } = useContext(CompanyRoleAssignmentContext);
  const { currentCompany } = useContext(CompanyContext);
  const { companyUsers, currentUser } = useContext(UserContext);
  const { stockLocationRoles, hasStockLocationRole } = useContext(StockLocationRoleAssignmentContext);

  const [importModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);

  const headers = [
    {
      key: 'email',
      name: t(TranslationKey.email).singular.upper,
      text: (item: CompanyRoleAssignment) => item.email,
    },
    {
      key: 'name',
      name: t(TranslationKey.name).singular.upper,
      text: (item: CompanyRoleAssignment) => {
        return `${companyUsers.get(item.userId)?.firstName} ${companyUsers.get(item.userId)?.lastName}` || '';
      },
    },
    {
      key: 'role',
      name: t(TranslationKey.role).singular.upper,
      text: (item: CompanyRoleAssignment) => {
        return (
          CompanyRoleAssignmentUtil.roleToString(item.role) +
          `${item.state === CompanyInviteState.accepted ? '' : ' (Invited)'}`
        );
      },
    },
  ];

  const currentCompanyRoles = [...companyRoles.values()].flat().filter(cr => cr.companyId === currentCompany.id);
  const shownCompanyRoles = useMemo(() => {
    if (hasCompanyRole(currentCompany.id, CompanyRole.administrator)) return currentCompanyRoles;

    const visibleUsers = new Set<string>();
    for (const [stockLocationId, roles] of stockLocationRoles) {
      if (hasStockLocationRole(currentCompany.id, stockLocationId, StockLocationRole.STOCK_LOCATION_MANAGER)) {
        roles.forEach(role => visibleUsers.add(role.userId));
      }
    }

    return currentCompanyRoles.filter(role => role.role === CompanyRole.administrator || visibleUsers.has(role.userId));
  }, [companyRoles, currentCompany]);

  const [items, setItems] = useState(shownCompanyRoles);

  if (!currentUser) return null;

  const filter = new CompanyRoleAssignmentFilter(shownCompanyRoles);

  return (
    <>
      {inviteModalOpen && <InviteUserModal open={inviteModalOpen} setOpen={setInviteModalOpen} />}
      {importModalOpen && <UserImportModal open={importModalOpen} setOpen={setImportModalOpen} />}

      <div className='content-x content-y-screen flex h-full flex-grow flex-col gap-8'>
        <p className='page-title'>{t(TranslationKey.userSettingsScreenTitle).singular.upper}</p>

        <div className='flex gap-8 text-black'>
          <div className='grid gap-2'>
            <p className='text-sm'>{t(TranslationKey.totalUsers).singular.upper}</p>
            <p className='text-xl'>{currentCompanyRoles.length}</p>
          </div>
          <div className='grid gap-2'>
            <p className='text-sm'>{t(TranslationKey.administrator).plural.upper}</p>
            <p className='text-xl'>{currentCompanyRoles.filter(cr => cr.role === CompanyRole.administrator).length}</p>
          </div>
          <div className='grid gap-2'>
            <p className='text-sm'>{t(TranslationKey.employee).plural.upper}</p>
            <p className='text-xl'>{currentCompanyRoles.filter(cr => cr.role === CompanyRole.employee).length}</p>
          </div>
        </div>

        <div className='flex flex-grow flex-col gap-4'>
          <div className='flex items-center justify-between'>
            <SearchFilter
              items={shownCompanyRoles}
              setItems={items => {
                const userIdSet = new Set(items.map(item => item.userId));
                setItems([...shownCompanyRoles.filter(cr => userIdSet.has(cr.userId))]);
              }}
              placeholder={t(TranslationKey.filterUsers).singular.label}
              filter={filter}
            />
            {isAdmin && (
              <div className='flex gap-1'>
                <Button
                  leading={<Icon size={'h-5 w-5'} type={IconType.userPlus1} />}
                  text={t(TranslationKey.inviteUser).plural.upper}
                  onClick={() => setInviteModalOpen(true)}
                />
                <ImportIconButton onClick={() => setImportModalOpen(true)} />
                <SettingsIconButton
                  onClick={() => navigate(Routing.settings.users.advanced.importConfigurations.overview())}
                />
              </div>
            )}
          </div>
          <Table
            items={items}
            headers={headers}
            onClick={
              isAdmin
                ? item => {
                    navigate(Routing.settings.users.update(item.id).roles());
                  }
                : undefined
            }
            filter={filter}
          />
        </div>
      </div>
    </>
  );
}
